/**
 * A SQL tokenizer for the formatter.
 *
 * It does not parse SQL; it splits text into the pieces a formatter must never break
 * apart — strings, quoted identifiers, comments, numbers, parameters, operators — so that
 * layout changes only the whitespace between them. Dialects differ in what opens those
 * pieces: MySQL's `#` and backticks, SQL Server's `[brackets]`, PostgreSQL's dollar
 * quoting and nested comments.
 */

export type SqlDialect =
  | "standard"
  | "postgresql"
  | "mysql"
  | "sqlserver"
  | "sqlite";

export type SqlTokenType =
  | "word"
  | "quoted"
  | "string"
  | "number"
  | "param"
  | "operator"
  | "comma"
  | "semicolon"
  | "open"
  | "close"
  | "bracketOpen"
  | "bracketClose"
  | "dot"
  | "lineComment"
  | "blockComment";

export interface SqlToken {
  type: SqlTokenType;
  text: string;
  /** Line breaks between the previous token and this one (0, 1, or 2 for a blank line). */
  nl: number;
}

interface Read {
  type: SqlTokenType;
  end: number;
}

const RE_WORD_START = /[A-Za-z_\u0080-￿]/;
const RE_WORD_PART = /[A-Za-z0-9_$\u0080-￿]/;
const RE_DIGIT = /[0-9]/;
const RE_STRING_PREFIX = /^(?:[EeNnBbXx]|[Uu]&)$/;
const RE_DOLLAR_TAG = /^\$([A-Za-z_\u0080-￿][A-Za-z0-9_\u0080-￿]*)?\$/;
const RE_NUMBER =
  /^(?:0[xX][0-9A-Fa-f]+|(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?)/;
const RE_NAMED_PARAM = /^:[A-Za-z_][A-Za-z0-9_]*/;
const RE_AT_PARAM = /^@@?[A-Za-z_#][A-Za-z0-9_$#]*/;
const RE_TEMP_TABLE = /^#{1,2}[A-Za-z_]/;
const RE_DOLLAR_PARAM = /^\$[0-9]+/;

/** Longest first, so `->>` wins over `->` and `-`. */
const OPERATORS: readonly string[] = [
  "<=>",
  "->>",
  "#>>",
  "!~*",
  "<<=",
  ">>=",
  "||",
  "&&",
  "<>",
  "!=",
  "<=",
  ">=",
  "::",
  ":=",
  "=>",
  "->",
  "#>",
  "@>",
  "<@",
  "<<",
  ">>",
  "**",
  "!~",
  "~*",
  "+",
  "-",
  "*",
  "/",
  "%",
  "=",
  "<",
  ">",
  "!",
  "~",
  "&",
  "|",
  "^",
  "@",
  "#",
  "?",
  ":",
];

/** The index just past a quoted run that starts at `start` with the quote at `quote`. */
function readQuoted(
  src: string,
  quote: string,
  start: number,
  backslash: boolean
): number {
  let i = start + 1;
  while (i < src.length) {
    const c = src[i];
    if (backslash && c === "\\") {
      i += 2;
    } else if (c === quote) {
      if (src[i + 1] === quote) {
        i += 2;
      } else {
        return i + 1;
      }
    } else {
      i++;
    }
  }
  return src.length;
}

function readBlockComment(src: string, start: number, nested: boolean): number {
  let depth = 1;
  let i = start + 2;
  while (i < src.length) {
    if (nested && src.startsWith("/*", i)) {
      depth++;
      i += 2;
    } else if (src.startsWith("*/", i)) {
      depth--;
      i += 2;
      if (depth === 0) {
        return i;
      }
    } else {
      i++;
    }
  }
  return src.length;
}

function readLineComment(src: string, start: number): number {
  const end = src.indexOf("\n", start);
  if (end < 0) {
    return src.length;
  }
  return src[end - 1] === "\r" ? end - 1 : end;
}

function readWord(src: string, start: number): number {
  let i = start + 1;
  while (i < src.length && RE_WORD_PART.test(src[i])) {
    i++;
  }
  return i;
}

function readDollarQuoted(src: string, start: number): number | null {
  const tag = RE_DOLLAR_TAG.exec(src.slice(start, start + 80));
  if (!tag) {
    return null;
  }
  const close = src.indexOf(tag[0], start + tag[0].length);
  return close < 0 ? src.length : close + tag[0].length;
}

function readBracketed(src: string, start: number): number {
  let i = start + 1;
  while (i < src.length) {
    if (src[i] === "]") {
      if (src[i + 1] === "]") {
        i += 2;
      } else {
        return i + 1;
      }
    } else {
      i++;
    }
  }
  return src.length;
}

function readWordLike(src: string, i: number, dialect: SqlDialect): Read {
  const end = readWord(src, i);
  const word = src.slice(i, end);
  // N'text', E'text', X'0F', U&'text': a prefix glued to a string is part of the string.
  if (src[end] === "'" && RE_STRING_PREFIX.test(word)) {
    const backslash =
      (dialect === "postgresql" && word.toLowerCase() === "e") ||
      dialect === "mysql";
    return { type: "string", end: readQuoted(src, "'", end, backslash) };
  }
  return { type: "word", end };
}

function readSymbol(src: string, i: number, dialect: SqlDialect): Read | null {
  const c = src[i];
  const pair = src.slice(i, i + 2);
  if (pair === "--") {
    return { type: "lineComment", end: readLineComment(src, i) };
  }
  if (pair === "/*") {
    return {
      type: "blockComment",
      end: readBlockComment(src, i, dialect === "postgresql"),
    };
  }
  if (c === "#" && dialect === "mysql") {
    return { type: "lineComment", end: readLineComment(src, i) };
  }
  if (c === "'") {
    return {
      type: "string",
      end: readQuoted(src, "'", i, dialect === "mysql"),
    };
  }
  if (c === '"') {
    return {
      type: "quoted",
      end: readQuoted(src, '"', i, dialect === "mysql"),
    };
  }
  if (c === "`") {
    return { type: "quoted", end: readQuoted(src, "`", i, false) };
  }
  if (c === "[" && (dialect === "sqlserver" || dialect === "sqlite")) {
    return { type: "quoted", end: readBracketed(src, i) };
  }
  return null;
}

function readDollar(src: string, i: number, dialect: SqlDialect): Read | null {
  if (src[i] !== "$") {
    return null;
  }
  if (dialect === "postgresql") {
    const end = readDollarQuoted(src, i);
    if (end !== null) {
      return { type: "string", end };
    }
  }
  const param = RE_DOLLAR_PARAM.exec(src.slice(i, i + 12));
  return param ? { type: "param", end: i + param[0].length } : null;
}

function readParam(src: string, i: number, dialect: SqlDialect): Read | null {
  if (src[i] === "?" && dialect !== "postgresql") {
    return { type: "param", end: i + 1 };
  }
  const rest = src.slice(i, i + 80);
  if (src[i] === ":" && src[i + 1] !== ":" && src[i - 1] !== ":") {
    const named = RE_NAMED_PARAM.exec(rest);
    if (named) {
      return { type: "param", end: i + named[0].length };
    }
  }
  if (src[i] === "@") {
    const at = RE_AT_PARAM.exec(rest);
    if (at) {
      return { type: "param", end: i + at[0].length };
    }
  }
  return null;
}

function readOperator(src: string, i: number): Read {
  for (const op of OPERATORS) {
    if (src.startsWith(op, i)) {
      return { type: "operator", end: i + op.length };
    }
  }
  return { type: "operator", end: i + 1 };
}

const PUNCTUATION: Readonly<Record<string, SqlTokenType>> = {
  "(": "open",
  ")": "close",
  ",": "comma",
  ";": "semicolon",
  "[": "bracketOpen",
  "]": "bracketClose",
};

function readToken(
  src: string,
  i: number,
  dialect: SqlDialect,
  prev: SqlToken | undefined
): Read {
  const c = src[i];
  const symbol = readSymbol(src, i, dialect);
  if (symbol) {
    return symbol;
  }
  if (Object.hasOwn(PUNCTUATION, c)) {
    return { type: PUNCTUATION[c], end: i + 1 };
  }
  if (RE_WORD_START.test(c)) {
    return readWordLike(src, i, dialect);
  }
  if (
    c === "#" &&
    dialect === "sqlserver" &&
    RE_TEMP_TABLE.test(src.slice(i, i + 3))
  ) {
    let end = i;
    while (src[end] === "#") {
      end++;
    }
    return { type: "word", end: readWord(src, end) };
  }
  const dotNumber = c === "." && RE_DIGIT.test(src[i + 1] ?? "");
  const afterName =
    prev !== undefined &&
    (prev.type === "word" || prev.type === "quoted" || prev.type === "close");
  if (RE_DIGIT.test(c) || (dotNumber && !afterName)) {
    const number = RE_NUMBER.exec(src.slice(i, i + 64));
    if (number) {
      return { type: "number", end: i + number[0].length };
    }
  }
  if (c === ".") {
    return { type: "dot", end: i + 1 };
  }
  return (
    readDollar(src, i, dialect) ??
    readParam(src, i, dialect) ??
    readOperator(src, i)
  );
}

/** Split SQL into tokens. Whitespace is dropped; line breaks are recorded on the next token. */
export function tokenizeSql(src: string, dialect: SqlDialect): SqlToken[] {
  const tokens: SqlToken[] = [];
  let i = 0;
  let nl = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === "\n") {
      nl++;
      i++;
    } else if (
      c === " " ||
      c === "\t" ||
      c === "\r" ||
      c === "\f" ||
      c === "\u{A0}"
    ) {
      i++;
    } else {
      const read = readToken(src, i, dialect, tokens.at(-1));
      tokens.push({
        type: read.type,
        text: src.slice(i, read.end),
        nl: Math.min(nl, 2),
      });
      i = read.end;
      nl = 0;
    }
  }
  return tokens;
}
